import PropTypes from 'prop-types';
import React from 'react';
import { connect } from 'react-redux';

import {
  isLoggedInFreeUser,
  userHasPaidTier,
} from '@wetransfer/transfer/helper/user';
import Browser from '@wetransfer/transfer/modules/concorde-browser';
import Actions from '@wetransfer/transfer/store/action';
import { TRANSFER_STATES } from '@wetransfer/transfer/transfer-bundle/constant/transfer-states';
import { getIsForwardedTransfer } from '@wetransfer/transfer/transfer-bundle/store/selectors';
import Helper from '@wetransfer/transfer/helper';

import Aborted, { getInactiveSubscriptionStatus } from './aborted';
import AccountEmailVerification from './account-email-verification';
import AccountEmailVerificationFailed from './account-email-verification-failed';
import Complete from './complete';
import EmailVerification from './email-verification';
import EmailVerificationResend from './email-verification-resend';
import EmailVerificationSuccess from './email-verification-success';
import ForceAccountVerification from './force-account-verification';
import MarketingEmailPrompt from './marketing-email-prompt';
import MobileUploader from './mobile-uploader';
import Progress from './progress';
import ReCaptcha from './recaptcha';
import UploaderForm from './UploaderForm';
import FFTransferInNameLogo from '@wetransfer/transfer/constant/flags/transfer-in-name-logo';
import { TransferInUploader } from './TransferInUploader/TransferInUploader';

export class Uploader extends React.Component {
  // If this component gets mounted we'll need to make sure
  // there is a Transfer in the session, so if there isn't
  // one we'll create a new one.
  componentDidMount() {
    const {
      addRecipient,
      changeType,
      create,
      setMessage,
      setDisplayName,
      route,
      transfer,
    } = this.props;

    // Do we already have a transfer in our session?
    if (transfer.state) {
      return;
    }
    create();

    const { to, msg, title } = route.query;

    if (to || msg || title) {
      if (to) {
        addRecipient(to);
      }

      if (msg) {
        setMessage(msg);
      }

      if (title) {
        setDisplayName(title);
      }

      changeType('email');
    }
  }

  componentWillUnmount() {
    if (Browser.isMobile) {
      // On mobile, uses can essentially navigate away from the tranfser window.
      // So we want to clear any transfer notifications so if users navigate back
      // to the upload process they start clean, and no notification tries to render.
      this.props.clearTransferNotification();

      // For the account verification process, when the Uploader components unmount
      // we want to reset the state to VERIFY_ACCOUNT, which is the first step of the
      // account verification process, so users start clean if they navigate away
      // and then back to the upload process.
      if (this.props.user.loggedIn && !this.props.user.isVerified) {
        this.props.setState(TRANSFER_STATES.VERIFY_ACCOUNT);
      }
    }
  }

  // Now let's find out what state our Transfer is in and what
  // component we'll actually be rendering today.
  render() {
    // Get our current Transfer and it's state.
    const {
      transfer,
      user,
      account,
      isForwardedTransfer,
      route,
      create,
      subscribeToMarketingEmails,
      channel,
    } = this.props;

    if (!transfer.state) {
      return null;
    }
    let transferState = transfer.state;
    let component = null;

    // For forwards we don't display the spinner
    if (isForwardedTransfer && transfer.state === 'started') {
      transferState = 'init';
    }

    // If the storage is already full, we'll immediately show this message
    if (transferState === 'init' && user.loggedIn && user.storageFull) {
      transferState = 'aborted';
      transfer.lastError = 'LimitReached';
    }

    // If a paid user is logged in, make sure their account is still active.
    if (
      userHasPaidTier(user) &&
      account.subscription &&
      !account.active_subscription
    ) {
      transferState = 'aborted';
      transfer.lastError = getInactiveSubscriptionStatus(account.subscription);
    }

    if (
      transfer.verification_required &&
      transfer.state !== TRANSFER_STATES.VERIFY_RESEND
    ) {
      transferState = 'verify';
    }

    // If the default recipient is not locked, it means the user is on their own channel
    const isDefaultRecipientLocked = Helper.isDefaultRecipientInUploadLocked({
      channelDefaultRecipient: channel.defaultRecipient,
      userLoggedIn: user.loggedIn,
      channelName: channel.name,
      accountSubDomainName: account.subdomain_name,
    });

    // If the user visits someone else's channel on desktop, show the Transfer In uploader state.
    // We only show it if the transfer is empty
    if (
      FFTransferInNameLogo.isOn() &&
      !Browser.isMobile &&
      isDefaultRecipientLocked &&
      transfer.files.length === 0
    ) {
      transferState = TRANSFER_STATES.TRANSFER_IN_UPLOADER;
    }

    switch (transferState) {
      // If we are not working on a transfer yet, or the transfer
      // has not yet started uploading - show the transfer form
      case 'init':
      default:
        component = Browser.isMobile ? MobileUploader : UploaderForm;
        break;

      // If we're not entirely sure if a transfer is spam
      case 'recaptcha':
        component = ReCaptcha;
        break;

      case TRANSFER_STATES.VERIFY:
        return (
          <EmailVerification
            prePopulatedEmail={transfer.sender}
            isLoggedIn={false}
          />
        );

      case TRANSFER_STATES.FORCE_ACCOUNT_VERIFICATION:
        return <ForceAccountVerification prePopulatedEmail={user.email} />;

      case TRANSFER_STATES.VERIFY_ACCOUNT:
        return <AccountEmailVerification prePopulatedEmail={user.email} />;

      case TRANSFER_STATES.VERIFY_ACCOUNT_FAILED:
        return <AccountEmailVerificationFailed />;

      case TRANSFER_STATES.VERIFY_RESEND:
        return (
          <EmailVerificationResend
            prePopulatedEmail={user.email || transfer.sender}
            isLoggedIn={user.loggedIn > 0}
            isVerifyingAccount={
              route.path === '/account-verification' && Boolean(route.query.sig)
            }
          />
        );

      // If it's started or paused, we'll show upload progress
      case 'started':
      case 'paused':
        component = Progress;
        break;

      case TRANSFER_STATES.VERIFY_ACCOUNT_SUCCESS:
        return isLoggedInFreeUser(user) && !user.hasEmailMarketing ? (
          <MarketingEmailPrompt
            skip={create}
            subscribe={subscribeToMarketingEmails}
          />
        ) : (
          <EmailVerificationSuccess create={create} user={user} />
        );

      case TRANSFER_STATES.TRANSFER_IN_UPLOADER:
        return <TransferInUploader channelName={channel.name} />;

      // If it's aborted, something went wrong. Unless we want to add
      // a button to cancel, then we might need something else again.
      case 'aborted':
        component = Aborted;
        break;

      // It's finished!
      case 'finished':
        component = Complete;
        break;
    }

    // Render the component with needed props
    const TransferStateComponentTag = component;

    return (
      <TransferStateComponentTag
        transfer={transfer}
        transferType={transfer.type}
        storageLimit={user.rules.storageLimit}
        {...this.props}
      />
    );
  }
}

Uploader.propTypes = {
  account: PropTypes.object,
  addRecipient: PropTypes.func.isRequired,
  changeType: PropTypes.func.isRequired,
  create: PropTypes.func.isRequired,
  subscribeToMarketingEmails: PropTypes.func.isRequired,
  transfer: PropTypes.oneOfType([PropTypes.object, PropTypes.bool]),
  route: PropTypes.object.isRequired,
  setMessage: PropTypes.func.isRequired,
  setDisplayName: PropTypes.func.isRequired,
  user: PropTypes.object,
  notification: PropTypes.object,
  session: PropTypes.object,
  channel: PropTypes.object,
  isForwardedTransfer: PropTypes.bool,
  clearTransferNotification: PropTypes.func.isRequired,
  setState: PropTypes.func.isRequired,
  isCookieOverlayActive: PropTypes.bool,
  transferWindowRef: PropTypes.object,
};

const mapStateToProps = (state) => ({
  account: state.account,
  layout: state.layout,
  transfer: state.transfer,
  route: state.route,
  session: state.session,
  user: state.user,
  notification: state.notification,
  channel: state.channel,
  isForwardedTransfer: getIsForwardedTransfer(state),
});

export default connect(
  mapStateToProps,
  {
    ...Actions.Transfer,
    clearTransferNotification: Actions.Notification.clearTransfer,
    subscribeToMarketingEmails: Actions.User.subscribeToMarketingEmails,
  },
  null,
  {
    pure: false,
  }
)(Uploader);
